import Long from 'long';
import { GetLevel } from './PPCBase';
import { PPCLevel } from './PPCI';
import { PPCPoint3D } from './PPCPoint3D';
import { BinaryTagInRecord, CardTagInRecord, CompositeTagInRecord, InInflate, RealTagInRecord, TagInRecord } from './PPCTag';
import { RedBlackTree } from './util/RedBlackTree';
import { Comparator } from './util/comparator';

const TAGCOMPOSITE = 0;
const TAGVARCARD = 0x40;
const TAGREAL = 0x80;
const TAGBINARY = 0xc0;
const TAGBOOLEAN = TAGVARCARD;
const TAGSTRING = TAGBINARY;

export const CHILDMASK = 0;
export const CHILDOFFSET = 1;
export const XYZ = 2;
export const RGB = 3;
export const INTENSITY = 4;
export const CLASSIFICATION = 5;
export const DIFFERENCE = 6;
export const SEGMENT = 7;
export const SOURCE = 8;

export const MAXATTRIBUTE = 8;

export class ChunkData {
  public mChunkId!: Long;
  public mLevel!: number;
  public mFileNameId!: Long;
  public mFileInfoPos!: number;
  public mMinVoxelId!: Long;
  public mMaxVoxelId!: Long;
  public mNrVoxels!: number;
  public mLowestVoxelId!: Long;
  public mHighestVoxelId!: Long;
  public mSubtreeInfo!: ChunkSubtreeInfo;
}

export class TreeInfo {
  public mBoxP1!: PPCPoint3D;
  public mBoxP2!: PPCPoint3D;
  public mTreeSize!: number;
  public mNrPoints!: Long;
  public mNrSourcePoints!: Long;
  public mDepth!: number;
  public mIs2D!: boolean;
}

export class TransformationInfo {
  public mDeltaX!: number;
  public mDeltaY!: number;
  public mDeltaZ!: number;
  public mScale!: number;
  public mDecimals!: number;
}

export class FieldInfo {
  public mMinIntensity!: number;
  public mMaxIntensity!: number;
  public mMinDifference!: number;
  public mMaxDifference!: number;
  public mFields!: boolean[];
}

export class ChunkSubtreeInfo {
  public mNrSubtrees!: number;
  public mSubtreeLevel!: number;
  public mSubtrees!: Array<ChunkSubtree>;
}

export class ChunkSubtree {
  public mRootId!: Long;
  public mNrVoxels!: number;
  public mFirstVoxel!: number; //1-based index
  public mLast!: boolean;
  public mLevel!: PPCLevel;
}

class ChunkDataComparator implements Comparator<ChunkData> {
  public compare(chData1: ChunkData, chData2: ChunkData): number {
    return chData1.mChunkId.compare(chData2.mChunkId);
  }
}
export class PPCHReader {
  private mChunkDataTree: RedBlackTree<ChunkData> = new RedBlackTree<ChunkData>(new ChunkDataComparator());
  //public mChunkDataArray : Array<ChunkData> = new Array<ChunkData>();
  public mTreeInfo: TreeInfo | null = null;
  public rootNode: TagInRecord | null = null;
  public mTransformationInfo: TransformationInfo | null = null;
  public mFieldInfo: FieldInfo | null = null;
  public mDataFolder!: string;
  public mDataFolderRelative!: boolean;
  public mHeaderFile!: string;

  public loadBuffer(buffer: ArrayBuffer) {
    if (buffer === null) {
      console.error('Could not read file');
      return;
    }
    if (buffer.byteLength < 256) {
      console.error('Not a valid header file');
      return;
    }
    const data = buffer.slice(256, buffer.byteLength);
    this.rootNode = InInflate(data);
    if (!(this.rootNode instanceof CompositeTagInRecord)) {
      console.error('InInflateTag returning error');
      return;
    }
    this.pickInformationFromTree(this.rootNode);
  }

  public loadAttribute(_filename: string) {
    // Note: fs.readFileSync is not available in browser context.
    // This method is a legacy stub for Node.js usage.
    console.error('loadAttribute is not supported in browser context');
  }

  private parseEnum8Node(node: CompositeTagInRecord, tagId: number, defaultValue: number, maxValue: number): number {
    if (node === undefined || node === null) {
      return defaultValue;
    }
    const cardNode = this.getChildNode(node, tagId);
    if (cardNode !== null && cardNode instanceof CardTagInRecord) {
      const value = cardNode.mCard.toInt();
      if (value >= 0 && value <= maxValue) {
        return value;
      }
    }
    return defaultValue;
  }

  private parseBooleanNode(node: CompositeTagInRecord, tagId: number, defaultValue: boolean): boolean {
    if (node === undefined || node === null) {
      return defaultValue;
    }
    const boolNode = this.getChildNode(node, tagId);
    if (boolNode === null) {
      return defaultValue;
    }
    if (boolNode instanceof CardTagInRecord) {
      return boolNode.mCard.isZero() ? false : true;
    }
    return false;
  }

  private parseCardNode(node: CompositeTagInRecord, tagId: number): number {
    const defaultValue = 0;
    if (node === undefined || node === null) {
      return defaultValue;
    }
    const cardNode = this.getChildNode(node, tagId);
    if (cardNode !== null && cardNode instanceof CardTagInRecord) {
      const value = cardNode.mCard.toInt();
      if (value >= 0) {
        return value;
      }
    }
    return defaultValue;
  }

  private parseLongNode(node: CompositeTagInRecord, tagId: number): Long {
    const defaultValue = Long.UZERO;

    if (node === undefined || node === null) {
      return defaultValue;
    }
    const longNode = this.getChildNode(node, tagId);
    if (longNode !== null && longNode instanceof CardTagInRecord) {
      return longNode.mCard;
    } else {
      return defaultValue;
    }
  }

  private parseRealNode(node: CompositeTagInRecord, tagId: number): number {
    const defaultValue = 0;
    if (node === undefined || node === null) {
      return defaultValue;
    }
    const realNode = this.getChildNode(node, tagId);
    if (realNode !== null && realNode instanceof RealTagInRecord) {
      return realNode.mReal;
    }
    return defaultValue;
  }

  private parseCoordsNode(node: CompositeTagInRecord, tagId: number): PPCPoint3D {
    const kTagX = 1 | TAGREAL;
    const kTagY = 2 | TAGREAL;
    const kTagZ = 3 | TAGREAL;

    const defaultValue = new PPCPoint3D(0, 0, 0);
    if (node === undefined || node === null) {
      return defaultValue;
    }

    const coordNode = this.getChildNode(node, tagId);
    if (coordNode !== null && coordNode instanceof CompositeTagInRecord) {
      const x = this.parseRealNode(coordNode, kTagX);
      const y = this.parseRealNode(coordNode, kTagY);
      const z = this.parseRealNode(coordNode, kTagZ);
      return new PPCPoint3D(x, y, z);
    }
    return defaultValue;
  }

  private pickInformationFromTree(root: CompositeTagInRecord) {
    if (root === undefined || root === null) {
      return;
    }
    if (!(root instanceof CompositeTagInRecord)) {
      return;
    }
    const kTreeInfoTag = 1;
    const kTagTransformation = 2;
    const kTagFieldInfo = 3;
    const kTagClassification = 4;
    const kTagChunkInfo = 5;

    const treeNode = this.getChildNode(root, kTreeInfoTag);
    if (treeNode !== null && treeNode instanceof CompositeTagInRecord) {
      this.parseTreeInfo(treeNode);
    }
    const transformationNode = this.getChildNode(root, kTagTransformation);
    if (transformationNode !== null && transformationNode instanceof CompositeTagInRecord) {
      this.mTransformationInfo = this.parseTransformationInfo(transformationNode);
    }
    const fieldInfoNode = this.getChildNode(root, kTagFieldInfo);
    if (fieldInfoNode !== null && fieldInfoNode instanceof CompositeTagInRecord) {
      this.mFieldInfo = this.parseFieldInfo(fieldInfoNode);
    }
    const classificationNode = this.getChildNode(root, kTagClassification);
    if (classificationNode !== null && classificationNode instanceof CompositeTagInRecord) {
      this.parseClassificationInfo(classificationNode);
    }
    const chunkInfoNode = this.getChildNode(root, kTagChunkInfo);
    if (chunkInfoNode !== null && chunkInfoNode instanceof CompositeTagInRecord) {
      this.parseChunkInfo(chunkInfoNode);
    }
    this.parseGeneralInfo(root);
  }

  private parseTransformationInfo(node: CompositeTagInRecord): TransformationInfo {
    const kTagDeltaX = 1 | TAGREAL;
    const kTagDeltaY = 2 | TAGREAL;
    const kTagDeltaZ = 3 | TAGREAL;
    const kTagScale = 4 | TAGREAL;
    const kTagDecimals = 5 | TAGVARCARD;

    const transformationInfo = new TransformationInfo();

    transformationInfo.mDeltaX = this.parseRealNode(node, kTagDeltaX);
    transformationInfo.mDeltaY = this.parseRealNode(node, kTagDeltaY);
    transformationInfo.mDeltaZ = this.parseRealNode(node, kTagDeltaZ);
    transformationInfo.mScale = this.parseRealNode(node, kTagScale);
    transformationInfo.mDecimals = this.parseCardNode(node, kTagDecimals);

    return transformationInfo;
  }

  private parseFieldInfo(node: CompositeTagInRecord): FieldInfo {
    const kTagAttributes = 1 | TAGCOMPOSITE;
    const kTagMinIntensity = 2 | TAGVARCARD;
    const kTagMaxIntensity = 3 | TAGVARCARD;
    const kTagMinDifference = 6 | TAGREAL;
    const kTagMaxDifference = 7 | TAGREAL;
    const fieldInfo = new FieldInfo();

    const ioNode = this.getChildNode(node, kTagAttributes);
    if (ioNode !== null && ioNode instanceof CompositeTagInRecord) {
      fieldInfo.mFields = this.parseFields(ioNode);
    }
    fieldInfo.mMinIntensity = this.parseCardNode(node, kTagMinIntensity) << 32;
    fieldInfo.mMaxIntensity = this.parseCardNode(node, kTagMaxIntensity) << 32;
    fieldInfo.mMinDifference = this.parseRealNode(node, kTagMinDifference);
    fieldInfo.mMaxDifference = this.parseRealNode(node, kTagMaxDifference);

    return fieldInfo;
  }

  private parseFields(node: CompositeTagInRecord): boolean[] {
    const kTagHasChildMask = 2 | TAGBOOLEAN;
    const kTagHasChildOffset = 3 | TAGBOOLEAN;
    const kTagHasRGB = 5 | TAGBOOLEAN;
    const kTagHasIntensity = 6 | TAGBOOLEAN;
    const kTagHasXYZ = 17 | TAGBOOLEAN;
    const kTagHasClassification = 9 | TAGBOOLEAN;
    const kTagHasDifference = 11 | TAGBOOLEAN;
    const kTagHasSegment = 19 | TAGBOOLEAN;
    const kTagHasSource = 20 | TAGBOOLEAN;

    const fields: boolean[] = new Array(MAXATTRIBUTE);

    fields[CHILDMASK] = this.parseBooleanNode(node, kTagHasChildMask, false);
    fields[CHILDOFFSET] = this.parseBooleanNode(node, kTagHasChildOffset, false);
    fields[RGB] = this.parseBooleanNode(node, kTagHasRGB, false);
    fields[INTENSITY] = this.parseBooleanNode(node, kTagHasIntensity, false);
    fields[XYZ] = this.parseBooleanNode(node, kTagHasXYZ, false);
    fields[CLASSIFICATION] = this.parseBooleanNode(node, kTagHasClassification, false);
    fields[DIFFERENCE] = this.parseBooleanNode(node, kTagHasDifference, false);
    fields[SEGMENT] = this.parseBooleanNode(node, kTagHasSegment, false);
    fields[SOURCE] = this.parseBooleanNode(node, kTagHasSource, false);

    return fields;
  }

  private parseClassificationInfo(node: CompositeTagInRecord) {
    const kTagClassificationListSize = 1 | TAGCOMPOSITE;
    const kTagCount = 1 | TAGVARCARD;
    const classificationSizeNode = this.getChildNode(node, kTagClassificationListSize);
    if (classificationSizeNode === null || !(classificationSizeNode instanceof CompositeTagInRecord)) {
      return;
    }
    if (!classificationSizeNode.mChildTags) return;
    const maxClass = classificationSizeNode.mChildTags.length;
    for (let i = 0; i < maxClass; i++) {
      const childNode = classificationSizeNode.mChildTags[i] as CompositeTagInRecord;
      if (childNode.getChildCount() !== 0) {
        this.parseCardNode(childNode, kTagCount);
      }
    }
  }

  private parseSubtreeInfo(node: CompositeTagInRecord): ChunkSubtreeInfo | undefined {
    const kTagSubtree7Count = 1 | TAGCOMPOSITE;

    const subtreeInfo = new ChunkSubtreeInfo();

    const subtreesNode = this.getChildNode(node, kTagSubtree7Count);
    if (subtreesNode === null || !(subtreesNode instanceof CompositeTagInRecord)) {
      return;
    }

    if (!subtreesNode.mChildTags) return;
    subtreeInfo.mNrSubtrees = subtreesNode.mChildTags.length;
    subtreeInfo.mSubtrees = new Array<ChunkSubtree>(subtreeInfo.mNrSubtrees);
    let total = 0;
    for (let i = 1; i <= subtreeInfo.mNrSubtrees; i++) {
      const subtreeNode = subtreesNode.mChildTags[i - 1] as CompositeTagInRecord;
      subtreeInfo.mSubtrees[i - 1] = this.parseSubtree(subtreeNode);
      subtreeInfo.mSubtrees[i - 1].mFirstVoxel = total + 1;
      total += subtreeInfo.mSubtrees[i - 1].mNrVoxels;
    }

    return subtreeInfo;
  }

  private parseSubtree(node: CompositeTagInRecord): ChunkSubtree {
    const kTagRootId = 1 | TAGVARCARD;
    const kTagNrVoxels = 2 | TAGVARCARD;
    const kTagLast = 3 | TAGBOOLEAN;

    const subtree = new ChunkSubtree();
    subtree.mRootId = this.parseLongNode(node, kTagRootId);
    subtree.mNrVoxels = this.parseCardNode(node, kTagNrVoxels);
    subtree.mFirstVoxel = 1;
    subtree.mLast = this.parseBooleanNode(node, kTagLast, true);
    subtree.mLevel = 7;

    return subtree;
  }

  private parseChunkDataNode(node: CompositeTagInRecord): ChunkData {
    const kTagChunkId = 1 | TAGVARCARD;
    const kTagFilenameId = 2 | TAGVARCARD;
    const kTagMaxVoxelId = 3 | TAGVARCARD;
    const kTagNrVoxels = 4 | TAGVARCARD;
    const kTagLowestVoxelId = 5 | TAGVARCARD;
    const kTagHighestVoxelId = 6 | TAGVARCARD;
    const kTagMinVoxelId = 9 | TAGVARCARD;
    const kTagSubtreeInfo = 10 | TAGCOMPOSITE;
    const kTagFileInfoPos = 14 | TAGVARCARD;

    const chunkData = new ChunkData();

    chunkData.mChunkId = this.parseLongNode(node, kTagChunkId);
    chunkData.mLevel = GetLevel(chunkData.mChunkId);
    chunkData.mFileNameId = this.parseLongNode(node, kTagFilenameId);
    chunkData.mFileInfoPos = this.parseCardNode(node, kTagFileInfoPos);
    chunkData.mMinVoxelId = this.parseLongNode(node, kTagMinVoxelId);
    if (chunkData.mMinVoxelId === Long.UZERO) {
      chunkData.mMinVoxelId = chunkData.mChunkId;
    }
    chunkData.mMaxVoxelId = this.parseLongNode(node, kTagMaxVoxelId);
    chunkData.mNrVoxels = this.parseCardNode(node, kTagNrVoxels);
    chunkData.mLowestVoxelId = this.parseLongNode(node, kTagLowestVoxelId);
    chunkData.mHighestVoxelId = this.parseLongNode(node, kTagHighestVoxelId);

    const subtreeInfoNode = this.getChildNode(node, kTagSubtreeInfo);
    // chunkData.mSubtreeInfo;
    if (subtreeInfoNode !== null && subtreeInfoNode instanceof CompositeTagInRecord) {
      const subtreeInfo = this.parseSubtreeInfo(subtreeInfoNode);
      if (subtreeInfo) {
        chunkData.mSubtreeInfo = subtreeInfo;
      }
    }

    return chunkData;
  }

  private parseChunkInfo(node: CompositeTagInRecord) {
    const kTagChunkCount = 1;
    const chunkCountNode = this.getChildNode(node, kTagChunkCount);
    if (chunkCountNode !== null && chunkCountNode instanceof CompositeTagInRecord) {
      if (!chunkCountNode.mChildTags) return;
      const chunkCount = chunkCountNode.mChildTags.length;
      for (let i = 0; i < chunkCount; i++) {
        const chunkDataNode = chunkCountNode.mChildTags[i];
        const chunkData = this.parseChunkDataNode(chunkDataNode as CompositeTagInRecord);
        this.mChunkDataTree.put(chunkData);
      }
    }
  }

  private parseGeneralInfo(node: CompositeTagInRecord) {
    const kTagDataFolder = 6 | TAGSTRING;
    const kTagDataFolderRelative = 7 | TAGBOOLEAN;
    const kHeaderFile = 8 | TAGSTRING;

    const decoder = new TextDecoder();
    const dataFolderNode = this.getChildNode(node, kTagDataFolder);
    if (dataFolderNode !== null && dataFolderNode instanceof BinaryTagInRecord) {
      this.mDataFolder = decoder.decode(new Uint8Array(dataFolderNode.mBinData));
      //console.log(this.mDataFolder);
    }
    const dataFolderRelativeNode = this.getChildNode(node, kTagDataFolderRelative);
    if (dataFolderRelativeNode !== null && dataFolderRelativeNode instanceof CardTagInRecord) {
      this.mDataFolderRelative = dataFolderRelativeNode.mCard.isZero() ? false : true;
      //console.log(this.mDataFolderRelative);
    }
    const headerFileNode = this.getChildNode(node, kHeaderFile);
    if (headerFileNode !== null && headerFileNode instanceof BinaryTagInRecord) {
      this.mHeaderFile = decoder.decode(new Uint8Array(headerFileNode.mBinData));
      //console.log(this.mHeaderFile);
    }
  }

  private getChildNode(node: CompositeTagInRecord, tagId: number): TagInRecord | null {
    if (node === undefined || node === null) {
      return null;
    }
    if (!node.mChildTags) return null;
    for (let i = 0; i < node.mChildTags.length; i++) {
      const child = node.mChildTags[i];
      if (child !== null && child.mTagId === tagId) {
        return child;
      }
    }
    return null;
  }

  private parseTreeInfo(node: CompositeTagInRecord) {
    if (node === undefined || node === null) {
      return;
    }
    const kTagBoxP1 = 1 | TAGCOMPOSITE; // 1
    const kTagBoxP2 = 2 | TAGCOMPOSITE; // 2
    const kTagTreeSize = 3 | TAGVARCARD; // 67
    const kTagNrPoints = 4 | TAGVARCARD; // 68
    const kTagNrSourcePoints = 6 | TAGVARCARD; // 70
    const kTagDepth = 5 | TAGVARCARD;
    const kTagType = 7 | TAGVARCARD;

    this.mTreeInfo = new TreeInfo();

    this.mTreeInfo.mBoxP1 = this.parseCoordsNode(node, kTagBoxP1);
    this.mTreeInfo.mBoxP2 = this.parseCoordsNode(node, kTagBoxP2);
    this.mTreeInfo.mTreeSize = this.parseCardNode(node, kTagTreeSize);
    this.mTreeInfo.mNrPoints = this.parseLongNode(node, kTagNrPoints);
    this.mTreeInfo.mNrSourcePoints = this.parseLongNode(node, kTagNrSourcePoints);
    this.mTreeInfo.mDepth = this.parseCardNode(node, kTagDepth);
    const type = this.parseEnum8Node(node, kTagType, 0, 1);
    this.mTreeInfo.mIs2D = type === 1;
  }

  public getNextChunkInfo(voxelId: Long): ChunkData | undefined {
    let p = this.mChunkDataTree.getRootNode();
    while (p !== null) {
      const chData = p.value;
      if (chData.mChunkId.comp(voxelId) <= 0 && chData.mMaxVoxelId.comp(voxelId) >= 0) {
        return chData;
      } else if (chData.mChunkId.compare(voxelId) > 0) {
        if (p.left !== null) {
          p = p.left;
        } else {
          return chData;
        }
      } else if (chData.mChunkId.compare(voxelId) < 0) {
        if (p.right !== null) {
          p = p.right;
        } else {
          p = this.mChunkDataTree.successor(p);
          if (p !== null) {
            return p.value;
          } else {
            return undefined;
          }
        }
      }
    }
    return undefined;
  }
}
